import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { Trash2 } from 'lucide-react';
import { ConfirmSheet } from '../ConfirmSheet';
import { replyLabel, type ChatMessage } from './messageUtils';
import { useT } from '../../i18n';

interface DeleteMessageDialogProps {
  message: ChatMessage | null;
  isOwn: boolean;
  /** The chat's family: the sender's face opens through it (D292). */
  familyId: string | null | undefined;
  /** Own message, or a family root/admin moderating (D23). */
  canDeleteForEveryone: boolean;
  onCancel: () => void;
  onDeleteForEveryone: () => void;
  onDeleteForMe: () => void;
}

// Delete for everyone (M20, D23) and delete for me (M22, D30), as a
// Living Room confirmation with the message lifted above the question.
export function DeleteMessageDialog({
  message,
  isOwn,
  familyId,
  canDeleteForEveryone,
  onCancel,
  onDeleteForEveryone,
  onDeleteForMe,
}: DeleteMessageDialogProps) {
  const t = useT();
  const alreadyDeleted = Boolean(message?.deletedAt);
  const forEveryone = canDeleteForEveryone && !alreadyDeleted;
  const sender = isOwn
    ? t('common.you')
    : (message?.sender?.displayName ?? t('chat.unknown'));

  const body = !forEveryone
    ? t('chat.delete.forMeBody')
    : isOwn
      ? t('chat.delete.forEveryoneBody')
      : message?.sender?.displayName
        ? t('chat.delete.adminBody', { name: message.sender.displayName })
        : t('chat.delete.adminBodyNoName');

  const preview = message && (
    <HStack
      spacing={3}
      p="10px"
      borderRadius="18px"
      bg="canvas"
      align="flex-start"
    >
      <Avatar
        size="sm"
        name={sender}
        src={isOwn ? undefined : (message.sender?.avatarUrl ?? undefined)}
        ownerId={message.sender?.id}
        familyId={familyId}
      />
      <Box minW={0}>
        <Text fontWeight={700} fontSize="15px">
          {sender}
        </Text>
        <Text fontSize="15px" color="ink.body" noOfLines={2}>
          {message.deletedAt ? t('chat.message.deleted') : replyLabel(message)}
        </Text>
      </Box>
    </HStack>
  );

  return (
    <ConfirmSheet
      isOpen={message !== null}
      onClose={onCancel}
      preview={preview}
      title={t(forEveryone ? 'chat.delete.title' : 'chat.delete.titleForMe')}
      body={body}
      confirmLabel={t(
        forEveryone ? 'chat.delete.forEveryone' : 'chat.delete.forMe'
      )}
      confirmIcon={<Trash2 size={18} />}
      onConfirm={forEveryone ? onDeleteForEveryone : onDeleteForMe}
      cancelLabel={t('chat.delete.keep')}
      extra={
        forEveryone && (
          <Button
            h="56px"
            fontSize="17px"
            variant="outline"
            colorScheme="gray"
            borderColor="danger"
            color="danger"
            bg="surface"
            onClick={onDeleteForMe}
          >
            {t('chat.delete.forMe')}
          </Button>
        )
      }
    />
  );
}
